import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./OrderPage.scss";
import * as itemService from "../../Services/Http/ItemService";
import * as orderService from "../../Services/Http/OrderService";
import { useOrder } from "../../Context/useOrder";
import { Product } from "../../Models/Product";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { PickItemRequest } from "../../Models/PickItemRequest";
import { ScanItemResponse } from "../../Models/ScanItemResponse";
import { PickCompleteRequest } from "../../Models/PickCompleteRequest";
import { PickItemResponse } from "../../Models/PicktemResponse";
import { UnpickItemRequest } from "../../Models/UnpickItemRequest";
import { BsTrash } from "react-icons/bs";
import { Button, Modal, NavLink } from "react-bootstrap";
import { Order } from "../../Models/Order";
import { LockUnlockResponse } from "../../Models/LockUnlockResponse";
import { GenericResponse } from "../../Models/GenericResponse";
import { dataContext } from "../../Context/contextProvider";
import { observer } from "mobx-react";
import ImgWithPlaceholder from "../../Components/ImgWithPlaceholder";
import ImageCarousel from "../../Components/ImageCarousel";
import OrderStatusBar from "../../Components/OrderStatusBar/OrderStatusBar";
import { LockUnlockRequest } from "../../Models/LockUnlockRequest";
import { SetOrderStatusRequest } from "../../Models/SetOrderStatusRequest";
import { useAuth } from "../../Context/useAuth";

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const questionSound = new Audio();
questionSound.src = "/assets/sounds/question.mp3";
questionSound.load();

type ScanObject = {
  binNo?: string;
  binCode?: string;
  locationNo?: string;
  serialNo?: string;
  stockCode?: string;
  stockNo?: string;
  stockDesc?: string;
  scanFactor?: string;
  status?: number; //Error = 0,Success = 1,BarcodeNotFound = 2,BinCodeNeeded = 3,StockCodeNotFoundInBin = 4,BinNotFound = 5,BinFoundWithWrongStock = 6
  scanResponseStatus?: ScanResponseStatusType;
  transNo?: string;
  orderCompanyNo?: number;
  detailNo?: number;
  pickQty?: number;
};

type ScanResponseStatusType = "error" | "success" | "scanbin";
const scanBarcodePlaceholderMessage = "Scan pallet or product barcode";
const scanBarcodeOrQryPlaceholderMessage = "Scan barcode or enter quantity";
const scanBinBarcodePlaceholderMessage = "Scan Bin barcode";
//tolerance for floating-point drift when fractional pack factors accumulate over repeated scans
const QTY_EPSILON = 1e-6;

const OrderPage = observer((props: Props) => {
  const { getCurrentOrder, addToCart } = useOrder();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const highlightedElementRef = useRef<HTMLElement | null>(null);
  const [modal1Open, setModal1Open] = useState<boolean>(false);
  const [modal2Open, setModal2Open] = useState<boolean>(false);
  const [modal3Open, setModal3Open] = useState<boolean>(false);
  const [modal4Open, setModal4Open] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [unpickingDetailNo, setUnpickingDetailNo] = useState<number | undefined>(undefined);
  const [palletsUsed, setPalletsUsed] = useState<string>("");
  const [completeOrderNumber, setCompleteOrderNumber] = useState<string>("");
  const [scanBarcodePlaceholder, setScanBarcodePlaceholder] = useState<string>(scanBarcodePlaceholderMessage);
  const scanObject = useRef<ScanObject | null>(null);
  const locked = useRef<boolean>(false);
  const finishButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const dataStore = useContext(dataContext);
  const { getSetting, getSettingNumber } = useAuth();

  useLayoutEffect(() => {
    if (searchBoxInputRef.current) {
      searchBoxInputRef.current.focus();
    }
  }, []);

  const lock = useCallback(
    async (order: Order | null): Promise<GenericResponse> => {
      return new Promise<GenericResponse>((resolve, reject) => {
        if (order) {
          const model: LockUnlockRequest = {
            transNo: order.transNo || "",
            orderCompanyNo: order.companyNo || 0,
          };
          orderService
            .lockOrderAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "")
            .then((lockResponse: LockUnlockResponse) => {
              if (!lockResponse) {
                const resp: GenericResponse = {
                  success: false,
                  errorMessage: "Couldn't acquire order lock.",
                };
                return resolve(resp);
              } else if (!lockResponse.success) {
                const resp: GenericResponse = {
                  success: false,
                  errorMessage: "Current order is locked by another user.",
                };
                return resolve(resp);
              } else if (lockResponse.success) {
                locked.current = true;
                const resp: GenericResponse = {
                  success: true,
                  errorMessage: "",
                };
                return resolve(resp);
              }
            })
            .catch((error: any) => {
              return reject(error);
            });
        } else {
          return reject("Order not found");
        }
      });
    },
    [dataStore.dbKey, dataStore.serverUrl],
  );

  const unlock = useCallback(
    async (order: Order | null): Promise<GenericResponse> => {
      return new Promise<GenericResponse>((resolve, reject) => {
        if (order) {
          const model: LockUnlockRequest = {
            transNo: order.transNo || "",
            orderCompanyNo: order.companyNo || 0,
          };
          orderService
            .unlockOrderAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "")
            .then((unlockResponse: LockUnlockResponse) => {
              if (!unlockResponse) {
                const resp: GenericResponse = {
                  success: false,
                  errorMessage: "Couldn't acquire unlock.",
                };
                return resolve(resp);
              } else if (!unlockResponse.success) {
                const resp: GenericResponse = {
                  success: false,
                  errorMessage: "There was an error unlocking the order.",
                };
                return resolve(resp);
              } else if (unlockResponse.success) {
                locked.current = false;
                const resp: GenericResponse = {
                  success: true,
                  errorMessage: "",
                };
                return resolve(resp);
              }
            })
            .catch((error: any) => {
              return reject(error);
            });
        } else {
          return reject("Order not found");
        }
      });
    },
    [dataStore.dbKey, dataStore.serverUrl],
  );

  const completeOrder = useCallback(
    (order: Order, setAmountOfPalletsUsed?: number, isCalledFromOnLoad?: boolean): void => {
      showIsComplete("");
      let currOrder: Order | null = order;
      if (!currOrder) {
        currOrder = getCurrentOrder();
      }
      if (!caches || !currOrder!.cartLines) {
        return;
      }

      // check if order not complete yet
      if (currOrder!.isPickComplete) {
        showIsComplete(currOrder!.transRefCode || "");
        return;
      }

      //all lines are complete
      const pickCompleteRequest: PickCompleteRequest = {
        companyNo: currOrder!.companyNo,
        transNo: currOrder!.transNo,
        amountOfPalletsUsed: !!setAmountOfPalletsUsed ? setAmountOfPalletsUsed : 0,
      };
      orderService
        .markOrderAsFullyPickedAPI(pickCompleteRequest, dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((resp: boolean) => {
          if (resp) {
            showIsComplete(currOrder!.transRefCode || "");
          } else {
            toast.warn(`Order ${currOrder!.transRefCode} is not complete`);
          }
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [dataStore.serverUrl, dataStore.dbKey, getCurrentOrder],
  );

  const isFullyPicked = useCallback((order: Order | null = null): boolean => {
    if (!order || !order!.cartLines) {
      return false;
    }
    const incompleteLines = order.cartLines.find((product) => (product!.qty || 0) < (product!.qtyOrdered || 0));
    return !incompleteLines;
  }, []);

  const checkIfOrderComplete = useCallback(
    (isCalledFromOnLoad?: boolean): void => {
      const order = getCurrentOrder();
      if (!order || !order!.cartLines) {
        return;
      }
      const _isFullyPicked = isFullyPicked(order);
      if (_isFullyPicked) {
        if (!!order.needsAmountOfPalletsUsed) {
          //ask user to enter amount of pallets used
          onShowModal1();
        } else {
          completeOrder(order, undefined, isCalledFromOnLoad);
        }
      }
    },
    [getCurrentOrder, isFullyPicked, completeOrder],
  );

  const parseDecimal = useCallback((n: number): number => {
    let decimalCount = countDecimals(n);
    if (decimalCount === 0) {
      return Math.floor(n);
    } else if (decimalCount > 3) {
      decimalCount = 3;
    }
    return parseFloat(n.toFixed(decimalCount));
  }, []);

  const onQtySuccessfullyChanged = useCallback(
    (addedQty: number, replacedQty: number, row: Product, highlightClass: string | null = "list-group-item-success"): void => {
      if (row.qty === undefined) {
        row.qty = 0;
      }
      if (row.qty < 0) {
        row.qty = 0;
      } else {
        row.qty = parseDecimal(row.qty);
      }
      row.qtyOrdered = row.qtyOrdered || 0;
      if (addedQty !== 0) {
        row.qty += addedQty;
      } else {
        row.qty = replacedQty;
      }
      if (row.qty < 0) {
        row.qty = 0;
      }
      if (row.qty > row.qtyOrdered) {
        row.qty = row.qtyOrdered;
      }
      const product = { ...row };
      const products: Product[] = [];
      products.push(product);
      addToCart(products, true);
      if (highlightClass) {
        scrollToProduct(product, highlightClass);
      }
    },
    [addToCart, parseDecimal],
  );
  const scrollToProduct = (product: Product, highlightClass: string = "list-group-item-success") => {
    if (product?.detailNo !== undefined) {
      const element = document.getElementById(`stockno_${product.detailNo}`);
      if (element) {
        if (searchBarRef.current) {
          const stickyTopOffset = parseFloat(window.getComputedStyle(searchBarRef.current).top) || 0;
          element.style.scrollMarginTop = `${stickyTopOffset + searchBarRef.current.offsetHeight + 8}px`;
        }
        element.scrollIntoView({
          behavior: "smooth",
        });
        element.classList.add(highlightClass);
        highlightedElementRef.current = element;
      }
      searchBoxInputRef.current?.focus();
    }
  };
  const handleUnpickLine = useCallback(
    (e: React.MouseEvent, product: Product): void => {
      e.stopPropagation();
      const order = getCurrentOrder();
      if (!order || !product.qty) {
        return;
      }
      if (!window.confirm(`Unpick ${product.stockDesc} [${product.stockCode}]?`)) {
        return;
      }
      const element = document.getElementById(`stockno_${product.detailNo}`);
      if (element) {
        element.classList.remove("list-group-item-success", "list-group-item-danger");
      }
      if (highlightedElementRef.current === element) {
        highlightedElementRef.current = null;
      }
      const model: UnpickItemRequest = {
        transNo: order.transNo,
        detailNo: product.detailNo,
        stockCode: product.stockCode,
        orderCompanyNo: order.companyNo,
      };
      setUnpickingDetailNo(product.detailNo);
      itemService
        .unpickItemAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((unpickItemResponse: PickItemResponse) => {
          if (unpickItemResponse.error) {
            errorSound.play();
            toast.error(unpickItemResponse.error, { autoClose: false });
          } else {
            onQtySuccessfullyChanged(0, unpickItemResponse.linePickQty || 0, product, "list-group-item-danger");
            toast.success(`Unpicked ${product.stockDesc}`, { autoClose: 1000 });
            checkIfOrderComplete();
          }
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        })
        .finally(() => {
          setUnpickingDetailNo(undefined);
        });
    },
    [getCurrentOrder, onQtySuccessfullyChanged, checkIfOrderComplete, dataStore.serverUrl, dataStore.dbKey],
  );
  const isOverPicked = useCallback((product: Product | undefined, addedQty: number): boolean => {
    if (!product || addedQty <= 0) {
      return false;
    }

    const orderedQty = product.qtyOrdered || 0;
    const currentQty = (product.qty || 0) + addedQty;
    //fractional pack factors (e.g. 1/3) accumulate floating-point rounding error over
    //repeated scans, so an exact match can come out as 40.00000000000003 - allow a tolerance
    return currentQty > orderedQty + QTY_EPSILON;
  }, []);

  const formatQtyExceededMessage = (product: Product, addedQty: number): string => {
    const packFactor = getPackFactor(product);
    const scannedQty = ((product.qty || 0) + addedQty) * packFactor;
    const orderedQty = (product.qtyOrdered || 0) * packFactor;
    return `Scanned quantity (${scannedQty.toLocaleString(navigator.language, { maximumFractionDigits: 3 })}) exceeds ordered quantity (${orderedQty.toLocaleString(navigator.language, { maximumFractionDigits: 3 })})`;
  };

  const doFirstScan = useCallback(
    (barcode: string): void => {
      scanObject.current = null; //reset

      const order = getCurrentOrder();
      if (!order) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not found", { autoClose: 200 });
        return;
      }

      itemService
        .scanItemFirstAPI(barcode, dataStore.serverUrl || "", dataStore.dbKey || "", order.companyNo, order.transNo)
        .then((scanItemResponse: ScanItemResponse) => {
          const scanresp: ScanResponseStatusType = showScanMessage(scanItemResponse);
          scanObject.current = {
            ...scanItemResponse,
            scanResponseStatus: scanresp,
            orderCompanyNo: order.companyNo,
            transNo: order.transNo,
          };

          if (scanresp === "error") {
            return;
          }

          const foundCartLine =
            order!.cartLines!.find(
              (x) => x.stockCode === scanItemResponse.stockCode && (x.qtyOrdered || 0) > (x.qty || 0),
            ) || order!.cartLines!.find((x) => x.stockCode === scanItemResponse.stockCode);
          if (!foundCartLine) {
            setSearchTerm("");
            errorSound.play();
            toast.error("Current order does not contain this product", {
              autoClose: 1000,
            });
            return;
          }
          if ((foundCartLine.qtyOrdered || 0) <= (foundCartLine.qty || 0)) {
            setSearchTerm("");
            errorSound.play();
            toast.error("All products with the current barcode have already been scanned", { autoClose: 2000 });
            return;
          }
          const addedQty: number = calculateAddedQty(scanItemResponse, foundCartLine);
          if (isOverPicked(foundCartLine, addedQty)) {
            setSearchTerm("");
            toast.error(formatQtyExceededMessage(foundCartLine, addedQty), {
              autoClose: 2000,
            });
            return;
          }
          scanObject.current = {
            ...scanObject.current,
            detailNo: foundCartLine.detailNo,
            stockCode: foundCartLine.stockCode,
            pickQty: addedQty,
          };
          if (scanresp === "scanbin") {
            setScanBarcodePlaceholder(scanBinBarcodePlaceholderMessage);
            return;
          } else {
            setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
          }
          const pickItemRequest: PickItemRequest = {
            transNo: order.transNo,
            detailNo: foundCartLine.detailNo,
            stockCode: foundCartLine.stockCode,
            pickQty: addedQty,
            serialNo: scanItemResponse.serialNo,
            binNo: scanItemResponse.binNo,
            locationNo: scanItemResponse.locationNo,
            orderCompanyNo: order.companyNo,
          };
          itemService
            .markItemAsScannedAPI(pickItemRequest, dataStore.serverUrl || "", dataStore.dbKey || "")
            .then((pickItemResponse: PickItemResponse) => {
              if (pickItemResponse.error) {
                setSearchTerm("");
                errorSound.play();
                toast.error(pickItemResponse.error, { autoClose: false });
              } else {
                setSearchTerm("");
                onQtySuccessfullyChanged(0, pickItemResponse.linePickQty || 0, foundCartLine);
                if (pickItemResponse.linePickQty) {
                  successSound.play();
                  toast.success(
                    `Added ${(addedQty * getPackFactor(foundCartLine)).toLocaleString(navigator.language, {
                      maximumFractionDigits: 3,
                    })}`,
                    { autoClose: 200 },
                  );
                } else {
                  toast.warn(`Not added (server error)`, { autoClose: 200 });
                }
                checkIfOrderComplete();
              }
            })
            .catch((error: any) => {
              setSearchTerm("");
              toast.error(error.message || error, { autoClose: false });
            });
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [checkIfOrderComplete, getCurrentOrder, onQtySuccessfullyChanged, isOverPicked, dataStore.serverUrl, dataStore.dbKey],
  );

  const doSecondScan = useCallback(
    (binCode: string): void => {
      const order = getCurrentOrder();
      if (!order) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not found", { autoClose: 200 });
        return;
      }

      const foundCartLine =
        order!.cartLines!.find(
          (x) => x.stockCode === scanObject.current?.stockCode && (x.qtyOrdered || 0) > (x.qty || 0),
        ) || order!.cartLines!.find((x) => x.stockCode === scanObject.current?.stockCode);
      if (!foundCartLine) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Current order does not contain this product", {
          autoClose: 1000,
        });
        return;
      }
      if ((foundCartLine.qtyOrdered || 0) <= (foundCartLine.qty || 0)) {
        setSearchTerm("");
        errorSound.play();
        toast.error("All products with the current barcode have already been scanned", { autoClose: 1000 });
        return;
      }

      itemService
        .scanItemSecondAPI(binCode, foundCartLine.stockCode!, dataStore.serverUrl || "", dataStore.dbKey || "", order.companyNo, order.transNo)
        .then((scanItemResponse: ScanItemResponse) => {
          const scanresp: ScanResponseStatusType = showScanMessage(scanItemResponse, true);
          scanObject.current = {
            ...scanObject.current,
            ...scanItemResponse,
            scanResponseStatus: scanresp,
          };
          if (scanresp === "error") {
            return;
          }

          const addedQty: number = calculateAddedQty(scanItemResponse, foundCartLine);
          if (isOverPicked(foundCartLine, addedQty)) {
            setSearchTerm("");
            toast.error(formatQtyExceededMessage(foundCartLine, addedQty), {
              autoClose: 2000,
            });
            return;
          }
          scanObject.current = {
            ...scanObject.current,
            detailNo: foundCartLine.detailNo,
            stockCode: foundCartLine.stockCode,
            pickQty: addedQty,
          };
          const pickItemRequest: PickItemRequest = {
            transNo: order.transNo,
            detailNo: foundCartLine.detailNo,
            stockCode: foundCartLine.stockCode,
            pickQty: addedQty,
            serialNo: scanItemResponse.serialNo,
            binNo: scanItemResponse.binNo,
            locationNo: scanItemResponse.locationNo,
            orderCompanyNo: order.companyNo,
          };
          if (scanresp === "scanbin") {
            setScanBarcodePlaceholder(scanBinBarcodePlaceholderMessage);
            return;
          } else {
            setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
          }
          itemService
            .markItemAsScannedAPI(pickItemRequest, dataStore.serverUrl || "", dataStore.dbKey || "")
            .then((pickItemResponse: PickItemResponse) => {
              if (pickItemResponse.error) {
                setSearchTerm("");
                errorSound.play();
                toast.error(pickItemResponse.error, { autoClose: false });
              } else {
                setSearchTerm("");
                onQtySuccessfullyChanged(0, pickItemResponse.linePickQty || 0, foundCartLine);
                if (pickItemResponse.linePickQty) {
                  successSound.play();
                  toast.success(
                    `Added ${(addedQty * getPackFactor(foundCartLine)).toLocaleString(navigator.language, {
                      maximumFractionDigits: 3,
                    })}`,
                    { autoClose: 200 },
                  );
                } else {
                  toast.warn(`Not added (server error)`, { autoClose: 200 });
                }
                checkIfOrderComplete();
              }
            })
            .catch((error: any) => {
              setSearchTerm("");
              toast.error(error.message || error, { autoClose: false });
            });
        })
        .catch((error: any) => {
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [checkIfOrderComplete, getCurrentOrder, onQtySuccessfullyChanged, isOverPicked, dataStore.serverUrl, dataStore.dbKey],
  );

  const doQtyScan = useCallback(
    (enteredQty: number): void => {
      if (enteredQty <= 0) {
        return;
      }

      const order = getCurrentOrder();
      if (!order) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not found", { autoClose: 1000 });
        return;
      }
      const foundCartLine =
        order!.cartLines!.find(
          (x) => x.stockCode === scanObject.current?.stockCode && (x.qtyOrdered || 0) > (x.qty || 0),
        ) || order!.cartLines!.find((x) => x.stockCode === scanObject.current?.stockCode);
      if (!foundCartLine) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Current order does not contain this product", {
          autoClose: 2000,
        });
        return;
      }
      if ((foundCartLine.qtyOrdered || 0) <= (foundCartLine.qty || 0)) {
        setSearchTerm("");
        errorSound.play();
        toast.error("All products with the current barcode have already been scanned", { autoClose: 2000 });
        return;
      }
      const enteredCartons = enteredQty / getPackFactor(foundCartLine);
      const newQty = enteredCartons - (scanObject.current?.pickQty || 0);
      if (newQty <= 0) {
        toast.error("New quantity must higher than previous quantity", {
          autoClose: 2000,
        });
        return;
      }

      if (isOverPicked(foundCartLine, newQty)) {
        setSearchTerm("");
        toast.error(formatQtyExceededMessage(foundCartLine, newQty), {
          autoClose: 2000,
        });
        return;
      }
      if (!scanObject.current) {
        setSearchTerm("");
        errorSound.play();
        toast.error("Order not current", { autoClose: 2000 });
        return;
      }
      const pickItemRequest: PickItemRequest = {
        transNo: scanObject.current.transNo,
        detailNo: scanObject.current.detailNo,
        stockCode: scanObject.current.stockCode,
        pickQty: newQty,
        serialNo: scanObject.current.serialNo,
        binNo: scanObject.current.binNo,
        locationNo: scanObject.current.locationNo,
        orderCompanyNo: scanObject.current.orderCompanyNo,
      };
      itemService
        .markItemAsScannedAPI(pickItemRequest, dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((pickItemResponse: PickItemResponse) => {
          scanObject.current = null; //reset
          if (pickItemResponse.error) {
            setSearchTerm("");
            errorSound.play();
            toast.error(pickItemResponse.error, { autoClose: false });
          } else {
            setSearchTerm("");
            onQtySuccessfullyChanged(0, pickItemResponse.linePickQty || 0, foundCartLine);
            successSound.play();
            toast.success(`Quantity updated`, { autoClose: 200 });
            setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
            checkIfOrderComplete();
          }
        })
        .catch((error: any) => {
          scanObject.current = null; //reset
          setSearchTerm("");
          toast.error(error.message || error, { autoClose: false });
        });
    },
    [checkIfOrderComplete, getCurrentOrder, onQtySuccessfullyChanged, isOverPicked, dataStore.serverUrl, dataStore.dbKey],
  );

  const showPickingBinCode = (): boolean => {
    const pickingBinField = getSetting(dataStore.serverUrl!, dataStore.dbKey!, "PDAPickingBinField");
    return pickingBinField !== undefined && pickingBinField !== "";
  };

  const showPendingIssueButton = (): boolean => {
    const pdaPickExcludePending = getSetting(dataStore.serverUrl!, dataStore.dbKey!, "PDAPickExcludePending");
    return pdaPickExcludePending !== undefined && pdaPickExcludePending !== null && (pdaPickExcludePending.toLowerCase() === "true" || pdaPickExcludePending === "1");
  };

  const getBarcodeQty = useCallback(
    (barcode: string): number => {
      if (!barcode || !barcode.trim() || isNaN(Number(barcode.trim()))) {
        return 0;
      }
      let amountOfDigits: number = getSettingNumber(dataStore.serverUrl!, dataStore.dbKey!, "PDAQtyDigits");
      if (amountOfDigits <= 0) {
        amountOfDigits = 2; // default to 2 digits if setting is not found or invalid
      }
      if (barcode.trim().length <= amountOfDigits) {
        return parseInt(barcode.trim(), 10);
      }
      return 0;
    },
    [dataStore.dbKey, dataStore.serverUrl, getSettingNumber],
  );

  const onBarcodeScanned = useCallback(
    (barcode: string): void => {
      if (highlightedElementRef.current) {
        highlightedElementRef.current.classList.remove("list-group-item-success", "list-group-item-danger");
        highlightedElementRef.current = null;
      }
      const order = getCurrentOrder();
      if (order && order.pickComplete) {
        toast.info("This order is fully picked.");
        return;
      }
      if (scanObject.current?.scanResponseStatus === "scanbin" && isBarcodeBinCode(barcode)) {
        doSecondScan(barcode);
        return;
      }

      if (scanObject.current?.scanResponseStatus === "success") {
        const enteredQty = getBarcodeQty(barcode);
        if (enteredQty > 0) {
          doQtyScan(enteredQty);
          return;
        }
      }
      doFirstScan(barcode);
    },
    [scanObject, doFirstScan, doSecondScan, doQtyScan, getCurrentOrder, getBarcodeQty],
  );

  const onKeyPressed = useCallback(
    (event: any) => {
      if (event.target && event.target instanceof HTMLInputElement && event.target.name === searchBoxInputRef.current!.name) {
        return;
      }
      const keyPressed = event.key ? event.key : "";
      if (keyPressed === "") {
        return;
      }
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          const newState = oldState;
          onBarcodeScanned(newState);
          return newState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => {
          const newState = oldState + keyPressed;
          return newState;
        });
      }
    },
    [onBarcodeScanned],
  );

  const bindGrid = useCallback(
    (order: Order | null = null) => {
      if (order) {
        lock(order)
          .then((resp: GenericResponse) => {
            if (resp.success) {
              if (order.pickComplete) {
                toast.info("This order is fully picked.");
              } else {
                checkIfOrderComplete(true);
              }
            } else {
              toast.error(resp.errorMessage, { autoClose: false });
              return navigate("/orders");
            }
          })
          .catch((error: any) => {
            toast.error(error.message || error, { autoClose: false });
          });
      }
    },
    [lock, navigate, checkIfOrderComplete],
  );

  useEffect(() => {
    const order = getCurrentOrder();
    bindGrid(order);
    return () => {
      if (!!locked.current) {
        unlock(order)
          .then((resp: GenericResponse) => {
            if (!resp.success) {
              toast.error(resp.errorMessage, { autoClose: false });
            }
          })
          .catch((error: any) => {
            toast.error(error.message || error, { autoClose: false });
          });
      }
    };
  }, [bindGrid, getCurrentOrder, unlock]);

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => {
      window.removeEventListener("keydown", onKeyPressed);
    };
  }, [onKeyPressed]);

  // useKeyDown((event: any) => {
  //     onKeyPressed(event);
  // }, []);

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onSearchClicked();
    }
  };

  const onSearchClicked = () => {
    onBarcodeScanned(searchTerm);
  };

  const countDecimals = (n: number): number => {
    if ((n as any) === null) {
      return 0;
    }
    if (Math.floor(n) === n) return 0;
    return n.toString().split(".")[1].length || 0;
  };
  const getPickedCssClassName = (product: Product): string => {
    let pickedCssClassName = "qty-badge badge";
    if (product.qtyOrdered === product.qty) {
      pickedCssClassName += " bg-success";
    } else {
      pickedCssClassName += " bg-secondary";
    }
    return pickedCssClassName;
  };
  const getShippedCssClassName = (product: Product): string => {
    let shippedCssClassName = "qty-badge badge";
    if (product.qtyOrdered === 0) {
      shippedCssClassName += " bg-success";
    } else {
      shippedCssClassName += " bg-secondary";
    }
    return shippedCssClassName;
  };
  const getShippedTextCssClassName = (product: Product): string => {
    let shippedCssClassName = "fw-bold ";
    if (product.qtyOrdered === 0) {
      shippedCssClassName += " text-success";
    } else {
      shippedCssClassName += " text-secondary";
    }
    return shippedCssClassName;
  };
  const showScanMessage = (scanItemResponse: ScanItemResponse, isSecondScan: boolean = false): ScanResponseStatusType => {
    let message = "";
    let status: ScanResponseStatusType = "error";
    if (scanItemResponse) {
      switch (scanItemResponse.status) {
        case 1:
          status = "success";
          message = "";
          break;
        case 2:
          message = "Barcode not found";
          break;
        case 3:
          if (isSecondScan) {
            message = "Bin not found";
          } else {
            message = "Now scan Bin";
            status = "scanbin";
          }
          break;
        case 4:
          message = "Stock code not found in Bin";
          break;
        case 5:
          message = "Bin not found";
          break;
        case 6:
          message = "Bin was found with wrong stock code";
          break;
        case 0:
        default:
          message = "Error occurred";
          break;
      }
    }

    setSearchTerm("");
    if (status === "success") {
      if (message) {
        toast.success(message, { autoClose: 2000 });
      }
    } else if (status === "error") {
      errorSound.play();
      toast.error(message, { autoClose: 2000 });
    } else if (status === "scanbin") {
      questionSound.play();
      toast.info(message, { autoClose: 2000 });
    }

    if (status === "success") {
      setScanBarcodePlaceholder(scanBarcodeOrQryPlaceholderMessage);
    } else {
      setScanBarcodePlaceholder(scanBarcodePlaceholderMessage);
    }
    return status;
  };

  const getPackFactor = (product: Product): number => {
    return (product.linePackQty || 1) * (product.linePackInnerQty || 1);
  };

  const calculateAddedQty = (scanItemResponse: ScanItemResponse, product: Product): number => {
    if (scanItemResponse.serialNo) {
      const stockQty = Number(scanItemResponse.stockQty);
      if (stockQty > 0) {
        const converted = stockQty / getPackFactor(product);
        const remaining = (product.qtyOrdered || 0) - (product.qty || 0);
        return remaining > 0 && converted > remaining ? remaining : converted;
      }
    }
    const packQty = product.linePackQty || 1;
    let addedQty: number = 0;
    switch ((scanItemResponse.scanFactor || "").toLowerCase()) {
      case "unit":
        addedQty = 1 / getPackFactor(product);
        break;
      case "ctn":
        addedQty = 1;
        break;
      case "inner":
        addedQty = 1 / packQty;
        break;
      case "serial":
        addedQty = product.scanSerialFactor || 1;
        break;
      case "bincode":
      default:
        addedQty = 1;
        break;
    }
    return addedQty;
  };

  const isBarcodeBinCode = (barcode: string): boolean => {
    const binRegex = new RegExp(/[A-Za-z][A-Za-z12]-[A-Za-z]-\d-\d\d/);
    return binRegex.test(barcode);
  };

  const handlePalletsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPalletsUsed(event.target.value);
    event.preventDefault();
  };
  const onShowModal1 = () => {
    setPalletsUsed("");
    setModal1Open(true);
  };
  const onCloseModal1 = () => {
    setModal1Open(false);
    setModal2Open(true);
  };
  const onModal2YesNo = (complete: boolean) => {
    setModal2Open(false);
    if (!complete) {
      setModal1Open(true);
    } else {
      const order = getCurrentOrder();
      if (!order || !order!.cartLines || !palletsUsed) {
        toast.error("Order not found. Request has not been processed", {
          autoClose: 1000,
        });
        return;
      }
      const numPalletsUsed = parseInt(palletsUsed, 10);
      setPalletsUsed("");
      completeOrder(order, numPalletsUsed);
    }
  };
  const onCloseModal2 = () => {
    setModal2Open(false);
    setPalletsUsed("");
  };
  const onCloseModal3 = () => {
    setModal3Open(false);
    setSelectedProduct(undefined);
  };
  const onCloseModal4 = () => {
    setModal4Open(false);
  };
  const onModal4YesNo = (confirmed: boolean) => {
    setModal4Open(false);
    if (confirmed) {
      handleSetPendingClick();
    }
  };
  const handleSetPendingClick = async () => {
    const order = getCurrentOrder();
    if (!order || !order!.cartLines) {
      return;
    }
    const model: SetOrderStatusRequest = {
      transNo: order.transNo || "",
      orderCompanyNo: order.companyNo || 0,
    };
    await orderService.setOrderPendingStatusAPI(model, dataStore.serverUrl!, dataStore.dbKey!);
    navigate("/orders");
  };
  const showIsComplete = (orderNo: string) => {
    setCompleteOrderNumber(orderNo);
    if (orderNo) {
      setTimeout(() => {
        if (finishButtonRef.current) {
          finishButtonRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
    }
  };
  const onFinish = () => {
    navigate("/orders");
  };
  return (
    <>
      <OrderStatusBar allowPrint={true} />
      <div className="container-limited-width">
        <div className="row sticky-top-3 search-bar" ref={searchBarRef}>
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="searchBox"
                type="text"
                className="form-control search-box"
                placeholder={scanBarcodePlaceholder}
                autoCapitalize="none"
                autoComplete="off"
                aria-describedby="btnGroupAddon"
                onKeyUp={handleKeyUp}
                onChange={handleSearchChange}
                value={searchTerm}
                ref={searchBoxInputRef}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-2 mt-2 p-0">
            <ul className="list-group list-group-flush p-0">
              {getCurrentOrder() &&
                getCurrentOrder()!.cartLines!.map((product, index) => {
                  return (
                    <li
                      id={`stockno_${product.detailNo}`}
                      key={`${product.stockNo}_${index}`}
                      className="list-group-item d-flex align-items-start p-1 product-list-item"
                      onClick={() => {
                        if (product.images) {
                          setSelectedProduct(product);
                          setModal3Open(true);
                        }
                      }}
                    >
                      <div className="d-flex flex-column product-image-container">
                        <div className="d-flex">
                          {product.images && product.images.length > 0 ? (
                            <ImgWithPlaceholder
                              src={product.images[0].thumbImageUrl}
                              alt={product.stockDesc}
                              className="product-image"
                              fallback={<img src="/assets/images/no-image-100x100.png" alt="" className="rounded product-image" />}
                            />
                          ) : null}
                        </div>
                        {product.showOutstanding && (
                          <div className="d-flex align-self-center align-items-center my-1">
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-row">
                                <span className={getPickedCssClassName(product)}>
                                  {((product.qty || 0) * getPackFactor(product)).toLocaleString(navigator.language, {
                                    maximumFractionDigits: 3,
                                  })}
                                  &nbsp;/&nbsp;
                                  {((product.qtyOrdered || 0) * getPackFactor(product)).toLocaleString(navigator.language, { maximumFractionDigits: 3 })}
                                </span>
                                <div className="d-flex pickunit align-items-center">{product.pickUnit}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-column flex-grow-1 mx-2">
                        <div className="fw-bold">{product.stockDesc}</div>
                        <div className="d-flex justify-content-between">
                          <div className="text-muted">{product.stockCode}</div>
                          {showPickingBinCode() && (
                            <div className="d-flex justify-content-center text-danger">
                              <b>{product.pickingBinCode}</b>
                            </div>
                          )}
                        </div>
                        <div className="d-flex flex-column">
                          <div className="text-muted">Barcode: {product.stockBarcode}</div>
                        </div>
                        {product.showShipped && (
                          <div className="d-flex justify-content-start my-1">
                            <div className="d-flex flex-column">
                              <div className="d-flex flex-row gap-2 align-items-center">
                                <span className={getShippedCssClassName(product)}>
                                  {product.qtyShipped!.toLocaleString(navigator.language, {
                                    maximumFractionDigits: 3,
                                  })}
                                  &nbsp;/&nbsp;
                                  {product.qtyToShip!.toLocaleString(navigator.language, { maximumFractionDigits: 3 })}
                                </span>
                                <div className={getShippedTextCssClassName(product)}>{product.qtyOrdered === 0 ? "SHIPPED" : "PART SHIPPED"}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-start">
                        {/* <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={!product.qty || unpickingDetailNo === product.detailNo}
                          onClick={(e) => handleUnpickLine(e, product)}
                          title="Unpick this line"
                        >
                          <BsTrash />
                        </Button> */}
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
        {completeOrderNumber && (
          <div className="row">
            <div className="col d-flex">
              <Button variant="success" className="d-flex flex-fill justify-content-center" onClick={onFinish} ref={finishButtonRef}>
                Order {completeOrderNumber} is complete
                <br />
                Click to continue
              </Button>
            </div>
          </div>
        )}
      </div>
      <Modal show={modal1Open} onHide={onCloseModal1} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Enter number of pallets used</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            name="pallets"
            type="number"
            min="1"
            className="form-control search-box"
            placeholder="Number of pallets used"
            autoCapitalize="none"
            value={palletsUsed}
            onChange={handlePalletsChange}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={onCloseModal1}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={modal2Open} onHide={onCloseModal2} backdrop="static" keyboard={false} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please confirm the number of pallets {palletsUsed} entered</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => onModal2YesNo(true)}>
            Confirm
          </Button>
          <Button variant="danger" onClick={() => onModal2YesNo(false)}>
            Re-enter
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={modal3Open} onHide={onCloseModal3} keyboard={true} fullscreen="xxl-down" aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header closeButton={true}>
          <Modal.Title>{selectedProduct?.stockDesc}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column justify-content-center">
            {selectedProduct && selectedProduct.images && selectedProduct.images.length > 0 && (
              <ImageCarousel imageUrls={selectedProduct.images?.filter((i) => i !== undefined && i.largeImageUrl !== undefined)?.map((i) => i.largeImageUrl!) || []} alt={selectedProduct.stockDesc} />
            )}
            {selectedProduct && (
              <div className="d-flex flex-column flex-grow-1 fw-bold mt-3">
                <div className="text-muted">StockCode: {selectedProduct.stockCode}</div>
                <div className="text-muted">Barcode: {selectedProduct.stockBarcode}</div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <Modal show={modal4Open} onHide={onCloseModal4} backdrop="static" keyboard={false} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Order will be marked as pending. Continue ?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => onModal4YesNo(true)}>
            Yes
          </Button>
          <Button variant="danger" onClick={() => onModal4YesNo(false)}>
            No
          </Button>
        </Modal.Footer>
      </Modal>
      <footer className="footer mt-5 fixed-bottom">
        <div className="container-fluid d-flex justify-content-around bottom-nav pt-2">
          {showPendingIssueButton() && (
            <NavLink className="d-flex flex-column" title="Pending Issue" onClick={() => setModal4Open(true)}>
              <i className="fa fa-pause footer-icon" aria-hidden="true"></i>
              <span>Pending Issue</span>
            </NavLink>
          )}
          <NavLink className="d-flex flex-column" title="Save Picking" onClick={() => navigate("/orders")}>
            <i className="fa fa-check footer-icon" aria-hidden="true"></i>
            <span>Save Picking</span>
          </NavLink>
        </div>
      </footer>
    </>
  );
});

export default OrderPage;
